import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useProducts } from '../context/ProductsContext';
import { ClipLoader } from 'react-spinners';
import ShipmentEditor from '../components/ShipmentEditor';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';
import './webhooks.css';

function deliveryCoreBaseUrl() {
  return process.env.REACT_APP_DELIVERY_CORE_API_URL || '';
}

function ordersRequestHeaders(token, user) {
  return buildConsoleRequestHeaders(token, user);
}

function formatTs(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function CalculationLifecycle({ calculation, shipments, statusHistory }) {
  const timings = calculation?.packer_response?.stage_timings_ms || {};
  const latestShipment = shipments?.[0];
  const statuses = new Set((statusHistory || []).map((event) => String(event.normalized_status || '').toUpperCase()));
  const currentStatus = String(statusHistory?.[statusHistory.length - 1]?.normalized_status || latestShipment?.status || 'DRAFT').toUpperCase();
  const registered = !!latestShipment;
  const delivered = statuses.has('DELIVERED') || currentStatus === 'DELIVERED';
  const inTransit = delivered || ['IN_TRANSIT', 'OUT_FOR_DELIVERY'].some((status) => statuses.has(status)) || ['IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(currentStatus);
  const stages = [
    { label: 'Request', value: 0, ready: true },
    { label: 'Packaging', value: timings.packaging, ready: Array.isArray(calculation?.packer_boxes) && calculation.packer_boxes.length > 0 },
    { label: 'Rates', value: timings.carrier_rates_and_pricing, ready: Array.isArray(calculation?.rates) && calculation.rates.length > 0 },
    { label: 'Registered', ready: registered },
    { label: 'In transit', ready: inTransit },
    { label: 'Delivered', ready: delivered },
  ];
  return <section className="calculation-progress calculation-lifecycle" aria-label="Order lifecycle">
    <header><div><span>ORDER LIFECYCLE</span><strong>{delivered ? 'Completed: delivered' : registered ? `In progress: ${currentStatus.toLowerCase().replaceAll('_', ' ')}` : (calculation?.rates || []).length ? 'Rates calculated, awaiting registration' : 'Calculation in progress'}</strong></div><i className={delivered ? 'delivered' : ''}>{delivered ? 'Completed' : 'In progress'}</i></header>
    <ol>{stages.map((stage, index) => <li className={stage.ready ? 'complete' : ''} key={stage.label}><i>{stage.ready ? '✓' : index + 1}</i><div><strong>{stage.label}</strong><span>{stage.value != null ? `${stage.value} ms` : stage.ready ? 'Done' : 'Pending'}</span></div></li>)}</ol>
  </section>;
}

function BoxVisualization({ calculation }) {
  const [mode, setMode] = useState('planned');
  const planned = Array.isArray(calculation?.packer_boxes) ? calculation.packer_boxes : [];
  const actual = Array.isArray(calculation?.actual_boxes) ? calculation.actual_boxes : [];
  const boxes = mode === 'actual' ? actual : planned;
  return <section className="box-visualization">
    <header><div><span>PACKAGING</span><strong>Boxes</strong></div><div className="box-mode"><button type="button" className={mode === 'planned' ? 'active' : ''} onClick={() => setMode('planned')}>Planned {planned.length}</button><button type="button" className={mode === 'actual' ? 'active' : ''} onClick={() => setMode('actual')}>Actual {actual.length}</button></div></header>
    {boxes.length ? <div className="box-cards">{boxes.map((box, index) => <article key={`${box.box_code}-${index}`}><div className="box-shape" style={{ '--box-width': `${Math.max(45, Math.min(90, Number(box.width_cm || 20) * 2))}px`, '--box-height': `${Math.max(36, Math.min(72, Number(box.height_cm || 16) * 2))}px` }}><span /></div><div><strong>{box.box_code || `Box ${index + 1}`}</strong><span>{box.length_cm || '—'} × {box.width_cm || '—'} × {box.height_cm || '—'} cm</span><small>{box.weight_kg || '—'} kg · Qty {box.qty || 1}</small></div></article>)}</div> : <p className="webhooks-muted">No {mode} boxes available.</p>}
  </section>;
}

function locationToDisplay(value, locale = undefined) {
  if (!value) return '—';
  if (typeof value === 'string') return value;

  const countryCodeRaw = value?.country_code ?? value?.countryCode ?? value?.country ?? value?.countryCodeAlpha2;
  const postalCode = value?.postal_code ?? value?.postalCode;

  const countryCode = countryCodeRaw ? String(countryCodeRaw).toUpperCase() : null;
  const countryName = (() => {
    if (!countryCode) return null;
    try {
      const dn = new Intl.DisplayNames([locale || undefined].filter(Boolean), { type: 'region' });
      return dn.of(countryCode) || countryCode;
    } catch {
      return countryCode;
    }
  })();

  if (countryName && postalCode) return `${countryName}, ${postalCode}`;
  if (countryName) return countryName;
  if (postalCode) return String(postalCode);
  return '—';
}

function normalizeParcelsList(parcels) {
  if (parcels == null) return [];
  if (Array.isArray(parcels)) return parcels;
  if (typeof parcels === 'object') return [parcels];
  return [];
}

function formatParcelWeightKg(weight) {
  const n = Number(weight);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(2);
}

function formatParcelLine(parcel) {
  if (!parcel || typeof parcel !== 'object') return '—';

  const length = parcel.length_cm ?? parcel.length;
  const width = parcel.width_cm ?? parcel.width;
  const height = parcel.height_cm ?? parcel.height;
  const weight = parcel.weight_kg ?? parcel.weight;

  const dims = [length, width, height].map((value) => {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : '—';
  });

  const weightStr = formatParcelWeightKg(weight);
  if (weightStr) return `${dims.join('x')}, ${weightStr}`;
  return dims.join('x');
}

function formatPackerBoxLine(box) {
  if (!box || typeof box !== 'object') return '—';

  const boxCode = box.box_code ?? box.boxCode;
  const length = box.length_cm ?? box.length;
  const width = box.width_cm ?? box.width;
  const height = box.height_cm ?? box.height;
  const weight = box.weight_kg ?? box.weight;
  const qty = Number(box.qty ?? box.quantity ?? 1);

  const dims = [length, width, height].map((value) => {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : '—';
  });

  const weightStr = formatParcelWeightKg(weight);
  const sizePart = weightStr ? `${dims.join('x')}, ${weightStr}` : dims.join('x');
  const codePart = boxCode != null && String(boxCode).trim() !== '' ? String(boxCode).trim() : null;
  const qtyPart = Number.isFinite(qty) && qty > 1 ? `×${qty}` : '';

  if (codePart) return `${codePart}${qtyPart}, ${sizePart}`;
  return sizePart;
}

function ParcelsDisplay({ parcels, packerBoxes, actualBoxes }) {
  const actual = normalizeParcelsList(actualBoxes);
  const planned = normalizeParcelsList(packerBoxes);
  const parcelList = normalizeParcelsList(parcels);
  const list = actual.length > 0 ? actual : (planned.length > 0 ? planned : parcelList);
  const formatter = actual.length > 0 || planned.length > 0 ? formatPackerBoxLine : formatParcelLine;

  if (list.length === 0) {
    return <span className="webhooks-muted">—</span>;
  }

  return (
    <div>
      {list.map((item, idx) => (
        <div key={idx}>{formatter(item)}</div>
      ))}
    </div>
  );
}

function formatBoxDimensionsOnly(box) {
  if (!box || typeof box !== 'object') return '—';

  const dims = [box.length_cm ?? box.length, box.width_cm ?? box.width, box.height_cm ?? box.height].map(
    (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? String(n) : '—';
    },
  );

  return dims.join('x');
}

function PlannedBoxesTable({ boxes, t }) {
  const list = normalizeParcelsList(boxes);

  if (list.length === 0) {
    return <div className="webhooks-muted">{t('boxesEditorNone')}</div>;
  }

  return (
    <div className="webhooks-tableWrap">
      <table className="webhooks-table">
        <thead>
          <tr>
            <th className="webhooks-th">{t('shipmentEditorBoxCode')}</th>
            <th className="webhooks-th">{t('shipmentEditorBoxQty')}</th>
            <th className="webhooks-th">{t('boxesEditorDimensions')}</th>
            <th className="webhooks-th">{t('boxesEditorWeight')}</th>
            <th className="webhooks-th">{t('boxesEditorVolume')}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((box, idx) => {
            const volume = box.volume_cm3 ?? box.volume;
            const volumeN = Number(volume);
            const volumeStr = Number.isFinite(volumeN) ? volumeN.toFixed(2) : '—';

            return (
              <tr
                key={`${box.box_code || 'box'}-${idx}`}
                className={idx === list.length - 1 ? 'webhooks-trLast' : undefined}
              >
                <td className="webhooks-td">{String(box.box_code ?? box.boxCode ?? '—')}</td>
                <td className="webhooks-td">{box.qty ?? box.quantity ?? 1}</td>
                <td className="webhooks-td">{formatBoxDimensionsOnly(box)}</td>
                <td className="webhooks-td">{formatParcelWeightKg(box.weight_kg ?? box.weight) || '—'}</td>
                <td className="webhooks-td">{volumeStr}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function actualBoxToRemovePayload(box) {
  if (!box || typeof box !== 'object') return null;

  const length = Number(box.length_cm ?? box.length);
  const width = Number(box.width_cm ?? box.width);
  const height = Number(box.height_cm ?? box.height);
  const weight = Number(box.weight_kg ?? box.weight);
  const volume = Number(box.volume_cm3 ?? box.volume);
  const boxCode = box.box_code ?? box.boxCode;

  if (!boxCode || String(boxCode).trim() === '') return null;

  return {
    box_code: String(boxCode).trim(),
    qty: 1,
    length_cm: Number.isFinite(length) ? length : 0,
    width_cm: Number.isFinite(width) ? width : 0,
    height_cm: Number.isFinite(height) ? height : 0,
    weight_kg: Number.isFinite(weight) ? weight : 0,
    volume_cm3: Number.isFinite(volume) ? volume : 0,
  };
}

function actualBoxRowKey(box, idx) {
  const code = box?.box_code ?? box?.boxCode ?? 'box';
  const length = box?.length_cm ?? box?.length ?? '';
  const width = box?.width_cm ?? box?.width ?? '';
  const height = box?.height_cm ?? box?.height ?? '';
  return `${code}-${length}x${width}x${height}-${idx}`;
}

function ActualBoxesTable({ boxes, t, theme, canEdit, removingKey, onRemove }) {
  const list = normalizeParcelsList(boxes);

  if (list.length === 0) {
    return <div className="webhooks-muted">{t('boxesEditorNone')}</div>;
  }

  return (
    <div className="webhooks-tableWrap">
      <table className="webhooks-table">
        <thead>
          <tr>
            <th className="webhooks-th">{t('shipmentEditorBoxCode')}</th>
            <th className="webhooks-th">{t('shipmentEditorBoxQty')}</th>
            <th className="webhooks-th">{t('boxesEditorDimensions')}</th>
            <th className="webhooks-th">{t('boxesEditorWeight')}</th>
            <th className="webhooks-th">{t('boxesEditorVolume')}</th>
            {canEdit && <th className="webhooks-th webhooks-thRight">{t('actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {list.map((box, idx) => {
            const volume = box.volume_cm3 ?? box.volume;
            const volumeN = Number(volume);
            const volumeStr = Number.isFinite(volumeN) ? volumeN.toFixed(2) : '—';
            const rowKey = actualBoxRowKey(box, idx);

            return (
              <tr
                key={rowKey}
                className={idx === list.length - 1 ? 'webhooks-trLast' : undefined}
              >
                <td className="webhooks-td">{String(box.box_code ?? box.boxCode ?? '—')}</td>
                <td className="webhooks-td">{box.qty ?? box.quantity ?? 1}</td>
                <td className="webhooks-td">{formatBoxDimensionsOnly(box)}</td>
                <td className="webhooks-td">{formatParcelWeightKg(box.weight_kg ?? box.weight) || '—'}</td>
                <td className="webhooks-td">{volumeStr}</td>
                {canEdit && (
                  <td className="webhooks-td webhooks-actionsCell">
                    <button
                      type="button"
                      className={[
                        'webhooks-iconBtn',
                        theme,
                        removingKey === rowKey ? 'disabled' : 'enabled',
                      ].join(' ')}
                      onClick={() => onRemove?.(box, rowKey)}
                      disabled={removingKey === rowKey}
                      aria-label={t('boxesEditorRemoveBox')}
                      title={t('boxesEditorRemoveBox')}
                    >
                      <span className="oi oi-trash" aria-hidden="true" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ACTUAL_BOX_WEIGHT_MIN_KG = 0.01;
const ACTUAL_BOX_WEIGHT_MAX_KG = 20;

function parseActualBoxWeightKg(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n < ACTUAL_BOX_WEIGHT_MIN_KG || n > ACTUAL_BOX_WEIGHT_MAX_KG) return null;
  return Math.round(n * 100) / 100;
}

function catalogBoxToPayload(box, weightKg) {
  if (!box || typeof box !== 'object') return null;

  const weight = parseActualBoxWeightKg(weightKg);
  if (weight === null) return null;

  const length = box.outer_length_cm ?? box.inner_length_cm;
  const width = box.outer_width_cm ?? box.inner_width_cm;
  const height = box.outer_height_cm ?? box.inner_height_cm;
  const lengthN = Number(length);
  const widthN = Number(width);
  const heightN = Number(height);
  const volumeRaw = box.volume ?? box.volume_cm3;
  let volumeN = Number(volumeRaw);
  if (!Number.isFinite(volumeN) && Number.isFinite(lengthN) && Number.isFinite(widthN) && Number.isFinite(heightN)) {
    volumeN = lengthN * widthN * heightN;
  }

  const name = box.name ?? box.id;
  if (!name || String(name).trim() === '') return null;

  return {
    box_code: String(name).trim(),
    qty: 1,
    length_cm: Number.isFinite(lengthN) ? lengthN : 0,
    width_cm: Number.isFinite(widthN) ? widthN : 0,
    height_cm: Number.isFinite(heightN) ? heightN : 0,
    weight_kg: weight,
    volume_cm3: Number.isFinite(volumeN) ? volumeN : 0,
  };
}

function formatCatalogBoxDimensions(box) {
  const length = box?.outer_length_cm ?? box?.inner_length_cm;
  const width = box?.outer_width_cm ?? box?.inner_width_cm;
  const height = box?.outer_height_cm ?? box?.inner_height_cm;
  return [length, width, height]
    .map((value) => {
      const n = Number(value);
      return Number.isFinite(n) ? String(n) : '—';
    })
    .join('x');
}

function AddActualBoxModal({
  calculationId,
  theme,
  t,
  user,
  onClose,
  onCreated,
}) {
  const { boxes, refreshProducts, loading: catalogLoading, boxesError } = useProducts();
  const [selectedBoxId, setSelectedBoxId] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const catalogBoxes = useMemo(
    () => (Array.isArray(boxes) ? boxes : []).filter((box) => box?.id),
    [boxes],
  );

  useEffect(() => {
    if (!selectedBoxId && catalogBoxes.length > 0) {
      setSelectedBoxId(catalogBoxes[0].id);
    }
  }, [catalogBoxes, selectedBoxId]);

  const selectedBox = useMemo(
    () => catalogBoxes.find((box) => box.id === selectedBoxId) ?? null,
    [catalogBoxes, selectedBoxId],
  );

  const parsedWeightKg = useMemo(() => parseActualBoxWeightKg(weightKg), [weightKg]);
  const canConfirm = catalogBoxes.length > 0 && parsedWeightKg !== null;

  const handleConfirm = async () => {
    if (!calculationId || !selectedBox || submitting || !canConfirm) return;

    if (weightKg.trim() === '') {
      setError('boxesEditorAddBoxWeightRequired');
      return;
    }

    const payload = catalogBoxToPayload(selectedBox, weightKg);
    if (!payload) {
      setError(parsedWeightKg === null ? 'boxesEditorAddBoxWeightInvalid' : 'boxesEditorAddBoxInvalid');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('ordersMissingToken');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(
        `${deliveryCoreBaseUrl()}/v1/calculations/${encodeURIComponent(calculationId)}/boxes/actual`,
        {
          method: 'POST',
          headers: {
            ...ordersRequestHeaders(token, user),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        if (res.status === 409) {
          throw new Error('boxesEditorActualLocked');
        }
        throw new Error(`HTTP ${res.status}`);
      }
      onCreated?.();
    } catch (err) {
      console.error('Failed to add actual box:', err);
      setError(err?.message === 'boxesEditorActualLocked' ? 'boxesEditorActualLocked' : 'boxesEditorAddBoxFailed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={['webhooks-modalOverlay', 'webhooks-modalOverlay--nested', theme].join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="webhooks-modal webhooks-modal--orderDetails">
        <div className="webhooks-modalHeader">
          <div className="webhooks-modalTitle">{t('boxesEditorAddBoxTitle')}</div>
          <button
            type="button"
            onClick={onClose}
            className={['webhooks-iconBtn', theme].join(' ')}
            aria-label={t('Cancel')}
            title={t('Cancel')}
            disabled={submitting}
          >
            ×
          </button>
        </div>
        <div className="webhooks-modalBody">
          {catalogLoading && <div className="webhooks-muted">{t('loading')}</div>}
          {boxesError && (
            <div className={['webhooks-error', theme].join(' ')}>{t(boxesError)}</div>
          )}
          {!catalogLoading && !boxesError && catalogBoxes.length === 0 && (
            <div className="webhooks-muted">{t('boxesEmpty')}</div>
          )}
          {!catalogLoading && catalogBoxes.length > 0 && (
            <div className="webhooks-formGrid">
              <div>
                <div className="webhooks-fieldLabel">{t('boxName')}</div>
                <select
                  className={['webhooks-select', theme].join(' ')}
                  value={selectedBoxId}
                  onChange={(e) => setSelectedBoxId(e.target.value)}
                  disabled={submitting}
                  style={{ colorScheme: theme === 'dark' ? 'dark' : 'light' }}
                >
                  {catalogBoxes.map((box) => (
                    <option key={box.id} value={box.id}>
                      {box.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedBox && (
                <div className="webhooks-formGrid">
                  <div>
                    <div className="webhooks-fieldLabel">{t('boxInnerDimensions')}</div>
                    <div className="webhooks-fieldValue">
                      {[selectedBox.inner_length_cm, selectedBox.inner_width_cm, selectedBox.inner_height_cm]
                        .map((value) => {
                          const n = Number(value);
                          return Number.isFinite(n) ? String(n) : '—';
                        })
                        .join('x')}
                    </div>
                  </div>
                  <div>
                    <div className="webhooks-fieldLabel">{t('boxOuterDimensions')}</div>
                    <div className="webhooks-fieldValue">{formatCatalogBoxDimensions(selectedBox)}</div>
                  </div>
                  <label className="webhooks-orderItemField">
                    <span className="webhooks-fieldLabel">{t('boxesEditorWeight')}</span>
                    <input
                      className={['webhooks-input', theme].join(' ')}
                      type="number"
                      min={ACTUAL_BOX_WEIGHT_MIN_KG}
                      max={ACTUAL_BOX_WEIGHT_MAX_KG}
                      step="0.01"
                      value={weightKg}
                      onChange={(e) => {
                        setWeightKg(e.target.value);
                        setError(null);
                      }}
                      disabled={submitting}
                      required
                      placeholder={t('boxesEditorAddBoxWeightHint')}
                    />
                  </label>
                </div>
              )}
            </div>
          )}
          {error && (
            <div className={['webhooks-error', theme, 'webhooks-sectionGap'].join(' ')}>
              {t(error)}
            </div>
          )}
        </div>
        <div className="webhooks-modalFooter">
          <button
            type="button"
            className={['webhooks-secondaryBtn', theme, submitting ? 'disabled' : 'enabled'].join(' ')}
            onClick={onClose}
            disabled={submitting}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            className={[
              'webhooks-primaryBtn',
              theme,
              submitting || !canConfirm ? 'disabled' : 'enabled',
            ].join(' ')}
            onClick={handleConfirm}
            disabled={submitting || !canConfirm}
          >
            {submitting ? t('loading') : t('boxesEditorAddBoxConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function calculationHasShipments(calculationId, shipments) {
  if (!calculationId) return false;
  return (Array.isArray(shipments) ? shipments : []).some(
    (shipment) => String(shipment?.calculation_id ?? '') === String(calculationId),
  );
}

function ActualBoxesSection({ calculationId, theme, t, user, canEdit, onChanged }) {
  const [actualBoxes, setActualBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [removingKey, setRemovingKey] = useState(null);

  const loadActualBoxes = useCallback(async () => {
    if (!calculationId) {
      setActualBoxes([]);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setActualBoxes([]);
      setError('ordersMissingToken');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${deliveryCoreBaseUrl()}/v1/calculations/${encodeURIComponent(calculationId)}/boxes/actual`,
        { headers: ordersRequestHeaders(token, user) },
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setActualBoxes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load actual boxes:', err);
      setActualBoxes([]);
      setError('boxesEditorActualLoadFailed');
    } finally {
      setLoading(false);
    }
  }, [calculationId, user]);

  useEffect(() => {
    loadActualBoxes();
  }, [loadActualBoxes]);

  const handleRemoveBox = useCallback(async (box, rowKey) => {
    if (!calculationId || !canEdit || removingKey) return;

    const payload = actualBoxToRemovePayload(box);
    if (!payload) {
      setError('boxesEditorRemoveBoxFailed');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('ordersMissingToken');
      return;
    }

    setRemovingKey(rowKey);
    setError(null);

    try {
      const res = await fetch(
        `${deliveryCoreBaseUrl()}/v1/calculations/${encodeURIComponent(calculationId)}/boxes/actual`,
        {
          method: 'DELETE',
          headers: {
            ...ordersRequestHeaders(token, user),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        if (res.status === 409) {
          throw new Error('boxesEditorActualLocked');
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setActualBoxes(Array.isArray(data) ? data : []);
      onChanged?.();
    } catch (err) {
      console.error('Failed to remove actual box:', err);
      setError(
        err?.message === 'boxesEditorActualLocked'
          ? 'boxesEditorActualLocked'
          : 'boxesEditorRemoveBoxFailed',
      );
    } finally {
      setRemovingKey(null);
    }
  }, [calculationId, canEdit, removingKey, user, onChanged]);

  const hasBoxes = actualBoxes.length > 0;

  return (
    <div className="webhooks-sectionGap">
      {canEdit && hasBoxes ? (
        <div className={['webhooks-tableTopbar', theme].join(' ')}>
          <div className="webhooks-fieldLabel" style={{ marginBottom: 0 }}>
            {t('boxesEditorSectionActual')}
          </div>
          <button
            type="button"
            className={['webhooks-secondaryBtn', theme, 'enabled'].join(' ')}
            onClick={() => setAddModalOpen(true)}
          >
            {t('boxesEditorAddBox')}
          </button>
        </div>
      ) : (
        <div className="webhooks-fieldLabel">{t('boxesEditorSectionActual')}</div>
      )}

      {!canEdit && (
        <div className="webhooks-muted webhooks-sectionGap">{t('boxesEditorActualLocked')}</div>
      )}

      {loading && <div className="webhooks-muted">{t('loading')}</div>}
      {error && !loading && (
        <div className={['webhooks-error', theme].join(' ')}>{t(error)}</div>
      )}

      {canEdit && !loading && !hasBoxes && (
        <button
          type="button"
          className={['webhooks-primaryBtn', theme, 'enabled'].join(' ')}
          onClick={() => setAddModalOpen(true)}
        >
          {t('boxesEditorAddBox')}
        </button>
      )}

      {!loading && hasBoxes && (
        <ActualBoxesTable
          boxes={actualBoxes}
          t={t}
          theme={theme}
          canEdit={canEdit}
          removingKey={removingKey}
          onRemove={handleRemoveBox}
        />
      )}

      {addModalOpen && canEdit && (
        <AddActualBoxModal
          calculationId={calculationId}
          theme={theme}
          t={t}
          user={user}
          onClose={() => setAddModalOpen(false)}
          onCreated={() => {
            setAddModalOpen(false);
            loadActualBoxes();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

function BoxesEditorModal({ calculation, shipments, theme, t, onClose, onActualBoxesChanged }) {
  const { user } = useAuth();

  if (!calculation) return null;

  const plannedBoxes = Array.isArray(calculation.packer_boxes) ? calculation.packer_boxes : [];
  const calculationId = calculation.id;
  const canEditActualBoxes = !calculationHasShipments(calculationId, shipments);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={['webhooks-modalOverlay', 'webhooks-modalOverlay--nested', theme].join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="webhooks-modal webhooks-modal--orderDetails">
        <div className="webhooks-modalHeader">
          <div className="webhooks-modalTitle">{t('boxesEditorTitle')}</div>
          <button
            type="button"
            onClick={onClose}
            className={['webhooks-iconBtn', theme].join(' ')}
            aria-label={t('Cancel')}
            title={t('Cancel')}
          >
            ×
          </button>
        </div>
        <div className="webhooks-modalBody">
          <div className="webhooks-fieldLabel">{t('boxesEditorSectionPlanned')}</div>
          <PlannedBoxesTable boxes={plannedBoxes} t={t} />
          <ActualBoxesSection
            calculationId={calculationId}
            theme={theme}
            t={t}
            user={user}
            canEdit={canEditActualBoxes}
            onChanged={onActualBoxesChanged}
          />
        </div>
      </div>
    </div>
  );
}

function pickupPointIdFromCalculation(calc) {
  const id = calc?.delivery_address?.pickup_point_id;
  if (id == null || String(id).trim() === '') return null;
  return String(id);
}

function numericCountryToAlpha2(numeric) {
  if (numeric == null) return null;
  const digits = String(numeric).replace(/[^\d]/g, '');
  if (!digits) return null;
  try {
    return countries.numericToAlpha2(digits.padStart(3, '0'));
  } catch {
    return null;
  }
}

function deliveryAddressToDisplay(deliveryAddress, locale = undefined) {
  if (!deliveryAddress || typeof deliveryAddress !== 'object') return '—';
  return locationToDisplay(
    {
      country_code: numericCountryToAlpha2(deliveryAddress.country),
      postal_code: deliveryAddress.postal_code ?? deliveryAddress.postalCode,
    },
    locale,
  );
}

function selectedRateFromCalculation(calc) {
  const selected = calc?.selected_rate ?? calc?.selectedRate;
  if (!selected || typeof selected !== 'object') return null;
  return selected;
}

function providerCodeFromCalculation(calc) {
  const rate = selectedRateFromCalculation(calc);
  const code = rate?.provider_code ?? rate?.providerCode;
  if (code == null || String(code).trim() === '') return null;
  return String(code);
}

function deliveryTypeFromCalculation(calc) {
  const rate = selectedRateFromCalculation(calc);
  const type = rate?.delivery_type ?? rate?.deliveryType;
  if (type == null || String(type).trim() === '') return null;
  return String(type);
}

function labelFieldsServiceType(shipment) {
  const labelFields = shipment?.label_fields;
  if (!labelFields || typeof labelFields !== 'object') return null;
  const service = labelFields.service ?? labelFields.fields?.service;
  const type = service?.type;
  if (type == null || String(type).trim() === '') return null;
  return String(type);
}

async function copyTextToClipboard(text) {
  const value = String(text || '');
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    // Fallback for older browsers / insecure contexts.
    try {
      const el = document.createElement('textarea');
      el.value = value;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.top = '-9999px';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return !!ok;
    } catch {
      return false;
    }
  }
}

function CopyableId({ id, theme, t }) {
  const full = id == null ? '' : String(id);
  const short = full ? full.slice(0, 8) : '—';

  return (
    <button
      type="button"
      className={['webhooks-idBtn', theme].join(' ')}
      title={full || undefined}
      onClick={async () => {
        if (!full) return;
        const ok = await copyTextToClipboard(full);
        if (ok) {
          alert(t('copiedToClipboard'));
        }
      }}
    >
      <span className="webhooks-idText">{short}</span>
    </button>
  );
}

function formatBool(value, t) {
  if (value === true) return t('yes');
  if (value === false) return t('no');
  return '—';
}

function formatJson(value) {
  if (value == null) return '—';
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return '—';
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatMoney(value, currency) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const code = currency ? String(currency).trim().toUpperCase() : '';
  if (!code) return n.toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${code}`;
  }
}

function formatDeliveryDays(min, max) {
  if (min == null && max == null) return '—';
  if (min != null && max != null && min !== max) return `${min}–${max}`;
  return String(min ?? max);
}

function isSelectedRate(rate, selectedRate) {
  if (!rate || !selectedRate) return false;
  const serviceA = String(rate.service_code ?? rate.serviceCode ?? '').trim();
  const serviceB = String(selectedRate.service_code ?? selectedRate.serviceCode ?? '').trim();
  if (!serviceA || !serviceB || serviceA !== serviceB) return false;
  const providerA = String(rate.provider_code ?? rate.providerCode ?? '').trim().toUpperCase();
  const providerB = String(selectedRate.provider_code ?? selectedRate.providerCode ?? '').trim().toUpperCase();
  if (providerA && providerB) return providerA === providerB;
  return true;
}

function RatesSection({ rates, selectedRate, theme, t }) {
  const rows = useMemo(() => {
    const list = (Array.isArray(rates) ? rates : []).filter((rate) => rate && typeof rate === 'object');
    if (list.length === 0) {
      return selectedRate ? [selectedRate] : [];
    }
    if (!selectedRate) {
      return list;
    }
    const selectedRows = [];
    const otherRows = [];
    list.forEach((rate) => {
      if (isSelectedRate(rate, selectedRate)) {
        selectedRows.push(rate);
      } else {
        otherRows.push(rate);
      }
    });
    return [...selectedRows, ...otherRows];
  }, [rates, selectedRate]);

  if (rows.length === 0) return null;

  return (
    <div className="webhooks-sectionGap">
      <div className="webhooks-fieldLabel">{t('ordersDetailsRates')}</div>
      <div className="webhooks-tableWrap webhooks-tableWrap--scrollX">
        <table className="webhooks-table webhooks-table--min800">
          <thead>
            <tr>
              <th className="webhooks-th webhooks-th--rateMark" aria-hidden="true" />
              <th className="webhooks-th">{t('ordersDetailsShipmentServiceCode')}</th>
              <th className="webhooks-th">{t('ordersDetailsCalcProvider')}</th>
              <th className="webhooks-th">{t('ordersDetailsCalcDeliveryType')}</th>
              <th className="webhooks-th">{t('ordersDetailsSelectedRateCustomerAmount')}</th>
              <th className="webhooks-th">{t('ordersDetailsSelectedRateCarrierAmount')}</th>
              <th className="webhooks-th">{t('ordersDetailsSelectedRateDeliveryDays')}</th>
              <th className="webhooks-th">{t('ordersDetailsShipmentPricingRule')}</th>
              <th className="webhooks-th">{t('ordersDetailsSelectedRateNotes')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((rate, idx) => {
              const currency = rate.carrier_currency ?? rate.carrierCurrency;
              const selected = isSelectedRate(rate, selectedRate);
              return (
                <tr
                  key={`${rate.service_code || 'rate'}-${rate.provider_code || 'provider'}-${idx}`}
                  className={[
                    idx === rows.length - 1 ? 'webhooks-trLast' : null,
                    selected ? `webhooks-trSelected ${theme}` : null,
                  ].filter(Boolean).join(' ')}
                >
                  <td className="webhooks-td webhooks-td--rateMark">
                    {selected ? (
                      <span className="webhooks-rateSelectedMark" aria-label={t('ordersDetailsSelectedRate')}>
                        ✓
                      </span>
                    ) : null}
                  </td>
                  <td className="webhooks-td">{rate.service_code || '—'}</td>
                  <td className="webhooks-td">{String(rate.provider_code || '—').toUpperCase()}</td>
                  <td className="webhooks-td">{rate.delivery_type || '—'}</td>
                  <td className="webhooks-td">{formatMoney(rate.customer_amount, currency)}</td>
                  <td className="webhooks-td">{formatMoney(rate.carrier_amount, currency)}</td>
                  <td className="webhooks-td">
                    {formatDeliveryDays(rate.delivery_days_min, rate.delivery_days_max)}
                  </td>
                  <td className="webhooks-td">
                    {rate.pricing_rule_id ? (
                      <CopyableId id={rate.pricing_rule_id} theme={theme} t={t} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="webhooks-td">{rate.notes || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CalculationSection({ calculations, shipments, showPickupPointColumn, theme, t, onActualBoxesChanged }) {
  const list = Array.isArray(calculations) ? calculations : [];
  const [boxesEditorCalculation, setBoxesEditorCalculation] = useState(null);

  if (list.length === 0) return null;

  return (
    <div>
      <div className="webhooks-fieldLabel">{t('deliveryParameters')}</div>
      <div className="webhooks-tableWrap">
        <table className="webhooks-table">
          <thead>
            <tr>
              <th className="webhooks-th">{t('ordersDetailsCalcCreated')}</th>
              <th className="webhooks-th">{t('ordersDetailsCalcProvider')}</th>
              <th className="webhooks-th">{t('ordersDetailsCalcDeliveryType')}</th>
              {showPickupPointColumn && (
                <th className="webhooks-th">{t('ordersDetailsCalcPickupPoint')}</th>
              )}
              <th className="webhooks-th">{t('ordersDetailsCalcOrigin')}</th>
              <th className="webhooks-th">{t('ordersDetailsCalcDestination')}</th>
              <th className="webhooks-th">{t('ordersDetailsCalcParcels')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((calc, idx) => {
              const requestPickupPointId = pickupPointIdFromCalculation(calc);
              return (
                <tr
                  key={calc?.id || idx}
                  className={idx === list.length - 1 ? 'webhooks-trLast' : undefined}
                >
                  <td className="webhooks-td">{formatTs(calc?.created_at)}</td>
                  <td className="webhooks-td">{String(providerCodeFromCalculation(calc) || '—').toUpperCase()}</td>
                  <td className="webhooks-td">{String(deliveryTypeFromCalculation(calc) || '—')}</td>
                  {showPickupPointColumn && (
                    <td className="webhooks-td">
                      {requestPickupPointId ? (
                        <CopyableId id={requestPickupPointId} theme={theme} t={t} />
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                  <td className="webhooks-td">
                    {locationToDisplay(calc?.origin)}
                  </td>
                  <td className="webhooks-td">
                    {deliveryAddressToDisplay(calc?.delivery_address)}
                  </td>
                  <td className="webhooks-td">
                    <div className="webhooks-inlineActions">
                      <ParcelsDisplay
                        parcels={calc?.parcels}
                        packerBoxes={calc?.packer_boxes}
                        actualBoxes={calc?.actual_boxes}
                      />
                      <button
                        type="button"
                        onClick={() => setBoxesEditorCalculation(calc)}
                        aria-label={t('boxesEditorOpen')}
                        title={t('boxesEditorOpen')}
                        className={['webhooks-iconBtn', theme].join(' ')}
                      >
                        <span className="oi oi-pencil" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {boxesEditorCalculation && (
        <BoxesEditorModal
          calculation={boxesEditorCalculation}
          shipments={shipments}
          theme={theme}
          t={t}
          onClose={() => setBoxesEditorCalculation(null)}
          onActualBoxesChanged={onActualBoxesChanged}
        />
      )}
    </div>
  );
}

function PayloadDetailsContent({ payload, theme, t }) {
  const items = Array.isArray(payload?.items) ? payload.items : [];

  return (
    <div className="webhooks-formGrid">
      <div>
        <div className="webhooks-fieldLabel">{t('ordersDetailsPayloadItems')}</div>
        {items.length === 0 ? (
          <div className="webhooks-muted">—</div>
        ) : (
          <div className="webhooks-tableWrap">
            <table className="webhooks-table">
              <thead>
                <tr>
                  <th className="webhooks-th">{t('ordersDetailsPayloadSku')}</th>
                  <th className="webhooks-th">{t('ordersDetailsPayloadQuantity')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr
                    key={`${item?.sku || 'item'}-${idx}`}
                    className={idx === items.length - 1 ? 'webhooks-trLast' : undefined}
                  >
                    <td className="webhooks-td">{item?.sku || '—'}</td>
                    <td className="webhooks-td">{item?.quantity ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <details className={['webhooks-details', theme].join(' ')} open>
        <summary className="webhooks-detailsSummary">
          <span className="webhooks-fieldLabel" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
            <span className="oi oi-warning webhooks-warningIcon" aria-hidden="true" />
            {t('ordersDetailsPayloadData')}
          </span>
        </summary>
        <div className="webhooks-detailsBody webhooks-fieldValue is-preWrap webhooks-clampText--mono">
          {formatJson(payload?.data)}
        </div>
      </details>
    </div>
  );
}

function PayloadDetailsModal({ payload, theme, t, onClose }) {
  if (!payload) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={['webhooks-modalOverlay', 'webhooks-modalOverlay--nested', theme].join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="webhooks-modal webhooks-modal--orderDetails">
        <div className="webhooks-modalHeader">
          <div className="webhooks-modalTitle">
            {t('ordersDetailsPayloadDetailsTitle')}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={['webhooks-iconBtn', theme].join(' ')}
            aria-label={t('Cancel')}
            title={t('Cancel')}
          >
            ×
          </button>
        </div>
        <div className="webhooks-modalBody">
          <PayloadDetailsContent payload={payload} theme={theme} t={t} />
        </div>
      </div>
    </div>
  );
}

function CalculationPayloadSection({ payload, calculations, shipments, showPickupPointColumn, theme, t, onActualBoxesChanged }) {
  const [payloadDetailsOpen, setPayloadDetailsOpen] = useState(false);

  if (!payload || typeof payload !== 'object') return null;

  return (
    <div className="webhooks-sectionGap">
      <div className="webhooks-fieldLabel">{t('ordersDetailsPayload')}</div>
      <div className="webhooks-formGrid">
        <div className="webhooks-tableWrap webhooks-tableWrap--scrollX">
          <table className="webhooks-table webhooks-table--min800">
            <thead>
              <tr>
                <th className="webhooks-th">{t('ordersOrderNumber')}</th>
                <th className="webhooks-th">{t('ordersCustomerId')}</th>
                <th className="webhooks-th">{t('Created')}</th>
                <th className="webhooks-th">{t('ordersConfirmed')}</th>
                <th className="webhooks-th">{t('userWidgetLanguage')}</th>
                <th className="webhooks-th webhooks-thRight">{t('ordersDetailsPayloadDetails')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webhooks-trLast">
                <td className="webhooks-td">{payload.order_number || '—'}</td>
                <td className="webhooks-td">{payload.customer_id || '—'}</td>
                <td className="webhooks-td">{formatTs(payload.created_at)}</td>
                <td className="webhooks-td">
                  {payload.confirmed_by_customer
                    ? formatTs(payload.confirmed_at)
                    : formatBool(payload.confirmed_by_customer, t)}
                </td>
                <td className="webhooks-td">{payload.language || '—'}</td>
                <td className="webhooks-td webhooks-actionsCell">
                  <button
                    type="button"
                    onClick={() => setPayloadDetailsOpen(true)}
                    aria-label={t('ordersDetailsPayloadDetailsOpen')}
                    title={t('ordersDetailsPayloadDetailsOpen')}
                    className={['webhooks-iconBtn', theme].join(' ')}
                  >
                    <span className="oi oi-ellipses" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <CalculationSection
          calculations={calculations}
          shipments={shipments}
          showPickupPointColumn={showPickupPointColumn}
          theme={theme}
          t={t}
          onActualBoxesChanged={onActualBoxesChanged}
        />
      </div>

      {payloadDetailsOpen && (
        <PayloadDetailsModal
          payload={payload}
          theme={theme}
          t={t}
          onClose={() => setPayloadDetailsOpen(false)}
        />
      )}
    </div>
  );
}

export default function OrderDetails({ order, onCreateShipmentAction, onDeleteAction }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const spinnerColor = theme === 'dark' ? 'white' : 'black';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [createShipmentSubmitting, setCreateShipmentSubmitting] = useState(false);
  const [createShipmentError, setCreateShipmentError] = useState(null);
  const [createShipmentConfirmOpen, setCreateShipmentConfirmOpen] = useState(false);

  const payloadId = order?.id || order?.raw?.id || '';
  const orderNumber = order?.order_number || order?.orderNumber || '';

  useEffect(() => {
    setSelectedShipment(null);
  }, [payloadId, orderNumber]);

  useEffect(() => {
    setSelectedShipment((current) => {
      if (!current?.id || !card) return current;
      const shipmentsList = Array.isArray(card.shipments) ? card.shipments : [];
      const updated = shipmentsList.find(
        (item) => String(item?.id ?? '') === String(current.id),
      );
      return updated && updated !== current ? updated : current;
    });
  }, [card]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!payloadId && !orderNumber) {
        setCard(null);
        setError(null);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setCard(null);
        setError('ordersMissingToken');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const url = payloadId
        ? `${deliveryCoreBaseUrl()}/v1/calculation-payloads/${encodeURIComponent(payloadId)}/order-card`
        : `${deliveryCoreBaseUrl()}/v1/orders/${encodeURIComponent(orderNumber)}`;
        let res = await fetch(url, {
          headers: ordersRequestHeaders(token, user),
        });

        if (res.status === 404 && payloadId && orderNumber) {
          res = await fetch(`${deliveryCoreBaseUrl()}/v1/orders/${encodeURIComponent(orderNumber)}`, {
            headers: ordersRequestHeaders(token, user),
          });
        }

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        if (!cancelled) {
          setCard(data);
          if (orderNumber) {
            const historyUrl = `${deliveryCoreBaseUrl()}/v1/order-status/history?external_order_number=${encodeURIComponent(orderNumber)}`;
            const historyResponse = await fetch(historyUrl, { headers: ordersRequestHeaders(token, user) });
            setStatusHistory(historyResponse.ok ? await historyResponse.json() : []);
          } else {
            setStatusHistory([]);
          }
        }
      } catch (err) {
        console.error('Failed to load order card:', err);
        if (!cancelled) {
          setCard(null);
          setError('ordersLoadFailed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [payloadId, orderNumber, user, reloadKey]);

  const calculationPayload = card?.calculation_payload || null;

  const calculations = Array.isArray(card?.calculations) ? card.calculations : [];
  const sortedCalculations = useMemo(() => {
    const list = Array.isArray(calculations) ? [...calculations] : [];
    list.sort((a, b) => {
      const aT = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bT = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return bT - aT;
    });
    return list;
  }, [calculations]);
  const calculationRates = useMemo(() => {
    const calc = sortedCalculations[0];
    return Array.isArray(calc?.rates) ? calc.rates : [];
  }, [sortedCalculations]);
  const selectedRate = useMemo(() => {
    if (card?.selected_rate) return card.selected_rate;
    const calc = sortedCalculations.find((item) => item?.selected_rate);
    return calc?.selected_rate ?? null;
  }, [card?.selected_rate, sortedCalculations]);
  const showPickupPointColumn = useMemo(
    () => sortedCalculations.some((calc) => pickupPointIdFromCalculation(calc)),
    [sortedCalculations],
  );
  const shipments = Array.isArray(card?.shipments) ? card.shipments : [];
  const sortedShipments = useMemo(() => {
    const list = Array.isArray(shipments) ? [...shipments] : [];
    list.sort((a, b) => {
      const aT = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bT = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return bT - aT;
    });
    return list;
  }, [shipments]);

  const primaryCalculation = sortedCalculations[0] ?? null;
  const calculationId = primaryCalculation?.id ?? null;

  const hasShipmentForCalculation = useMemo(() => {
    if (!calculationId) return false;
    return sortedShipments.some(
      (shipment) => String(shipment?.calculation_id ?? '') === String(calculationId),
    );
  }, [sortedShipments, calculationId]);

  const canCreateShipment = useMemo(() => {
    if (loading || !card || !calculationId) return false;
    if (!primaryCalculation?.selected_rate) return false;
    if (hasShipmentForCalculation) return false;
    return true;
  }, [loading, card, calculationId, primaryCalculation, hasShipmentForCalculation]);

  const canDeleteCalculation = useMemo(() => {
    if (loading || !calculationPayload?.id) return false;
    if (calculationPayload.confirmed_by_customer) return false;
    const payloadCalcId = String(calculationPayload.id);
    return !sortedShipments.some(
      (shipment) => String(shipment?.calculation_id ?? '') === payloadCalcId,
    );
  }, [loading, calculationPayload, sortedShipments]);

  const handleCreateShipment = useCallback(() => {
    if (!calculationId || createShipmentSubmitting) return;
    setCreateShipmentConfirmOpen(true);
  }, [calculationId, createShipmentSubmitting]);

  const executeCreateShipment = useCallback(async () => {
    if (!calculationId || createShipmentSubmitting) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setCreateShipmentError('ordersMissingToken');
      setCreateShipmentConfirmOpen(false);
      return;
    }

    setCreateShipmentConfirmOpen(false);
    setCreateShipmentSubmitting(true);
    setCreateShipmentError(null);

    try {
      const headers = {
        ...ordersRequestHeaders(token, user),
        'Content-Type': 'application/json',
      };
      const requestsRes = await fetch(
        `${deliveryCoreBaseUrl()}/v1/calculations/${encodeURIComponent(calculationId)}/shipment-requests`,
        { headers },
      );
      if (!requestsRes.ok) {
        throw new Error(`HTTP ${requestsRes.status}`);
      }

      const shipmentRequest = await requestsRes.json();
      if (!shipmentRequest || typeof shipmentRequest !== 'object' || Array.isArray(shipmentRequest)) {
        throw new Error('Invalid shipment request');
      }

      const postRes = await fetch(`${deliveryCoreBaseUrl()}/v1/shipments`, {
        method: 'POST',
        headers: {
          ...headers,
          'Idempotency-Key': `console-create:${calculationId}`,
        },
        body: JSON.stringify(shipmentRequest),
      });
      if (!postRes.ok) {
        throw new Error(`HTTP ${postRes.status}`);
      }

      setReloadKey((key) => key + 1);
    } catch (err) {
      console.error('Failed to create shipment:', err);
      setCreateShipmentError('ordersCreateFailed');
    } finally {
      setCreateShipmentSubmitting(false);
    }
  }, [calculationId, createShipmentSubmitting, user]);

  useEffect(() => {
    if (!onCreateShipmentAction) return;
    onCreateShipmentAction({
      canCreate: canCreateShipment,
      submitting: createShipmentSubmitting,
      onCreate: handleCreateShipment,
    });
  }, [
    canCreateShipment,
    createShipmentSubmitting,
    handleCreateShipment,
    onCreateShipmentAction,
  ]);

  useEffect(() => {
    if (!onDeleteAction) return;
    onDeleteAction({
      canDelete: canDeleteCalculation,
      payloadId: calculationPayload?.id || null,
    });
  }, [canDeleteCalculation, calculationPayload?.id, onDeleteAction]);

  return (
    <div>
      {loading && <div className="webhooks-muted">{
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100%',
          padding: 24,
          }}
        >
          <ClipLoader color={spinnerColor} size={32} aria-label="Loading" />
        </div>
        }
        </div>
      }
      {!loading && error && <div className={['webhooks-error', theme].join(' ')}>{t(error)}</div>}
      {!loading && createShipmentError && (
        <div className={['webhooks-error', theme].join(' ')}>{t(createShipmentError)}</div>
      )}

      {!loading && !error && !card && <p className="webhooks-muted">{t('orderDetailsPlaceholder')}</p>}

      {!!card && (
        <div className="webhooks-formGrid">
          {primaryCalculation && <CalculationLifecycle calculation={primaryCalculation} shipments={sortedShipments} statusHistory={statusHistory} />}
          {primaryCalculation && <BoxVisualization calculation={primaryCalculation} />}
          <CalculationPayloadSection
            payload={calculationPayload}
            calculations={sortedCalculations}
            shipments={sortedShipments}
            showPickupPointColumn={showPickupPointColumn}
            theme={theme}
            t={t}
            onActualBoxesChanged={() => setReloadKey((key) => key + 1)}
          />

          <RatesSection rates={calculationRates} selectedRate={selectedRate} theme={theme} t={t} />

          <div className="shipment-journey webhooks-sectionGap">
            <div className="webhooks-fieldLabel">Shipment journey</div>
            {statusHistory.length ? <ol>{[...statusHistory].sort((a, b) => new Date(a.occurred_at || a.created_at) - new Date(b.occurred_at || b.created_at)).map((event) => <li key={event.id || `${event.normalized_status}-${event.occurred_at}`}>
              <i className={String(event.normalized_status).toUpperCase() === 'DELIVERED' ? 'complete' : ''} />
              <div><strong>{event.normalized_status || event.provider_status || 'Status update'}</strong><span>{event.description || event.provider_status_code || event.event_source}</span></div>
              <time>{formatTs(event.occurred_at || event.created_at)}</time>
            </li>)}</ol> : <div className="webhooks-muted">No tracking events yet. The journey starts when a shipment is registered.</div>}
          </div>

          {sortedShipments.length > 0 && (
            <div className="webhooks-sectionGap">
              <div className="webhooks-fieldLabel">{t('ordersDetailsShipments')}</div>
              <div className="webhooks-tableWrap webhooks-sectionGap">
                <table className="webhooks-table">
                  <thead>
                    <tr>
                      <th className="webhooks-th">{t('ordersDetailsShipmentId')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentCreated')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentStatus')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentProvider')}</th>
                      <th className="webhooks-th">{t('ordersDetailsCarrierOrderNumber')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentTracking')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentServiceCode')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentServiceType')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentPricingRule')}</th>
                      <th className="webhooks-th">{t('ordersDetailsShipmentUpdated')}</th>
                      <th className="webhooks-th webhooks-thRight">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedShipments.map((s, idx) => (
                      <tr
                        key={s?.id || idx}
                        className={idx === sortedShipments.length - 1 ? 'webhooks-trLast' : undefined}
                      >
                        <td className="webhooks-td">
                          <CopyableId id={s?.id} theme={theme} t={t} />
                        </td>
                        <td className="webhooks-td">{formatTs(s?.created_at)}</td>
                        <td className="webhooks-td">{String(s?.status || '—')}</td>
                        <td className="webhooks-td">{String(s?.provider_code || '—').toUpperCase()}</td>
                        <td className="webhooks-td">
                          <div className="webhooks-clampText webhooks-clampText--mono is-expanded">
                            {String(s?.carrier_order_number || '—')}
                          </div>
                        </td>
                        <td className="webhooks-td">
                          <div className="webhooks-clampText webhooks-clampText--mono is-expanded">
                            {String(
                              (Array.isArray(s?.tracking_numbers) && s.tracking_numbers.filter(Boolean).join(', '))
                              || s?.tracking_number
                              || '—'
                            )}
                          </div>
                        </td>
                        <td className="webhooks-td">{String(s?.service_code || '—')}</td>
                        <td className="webhooks-td">{labelFieldsServiceType(s) || '—'}</td>
                        <td className="webhooks-td">
                          {s?.pricing_rule_id ? (
                            <CopyableId id={s.pricing_rule_id} theme={theme} t={t} />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="webhooks-td">{formatTs(s?.updated_at)}</td>
                        <td className="webhooks-td webhooks-actionsCell">
                          <button
                            type="button"
                            onClick={() => setSelectedShipment(s)}
                            aria-label={t('shipmentEditorOpen')}
                            title={t('shipmentEditorOpen')}
                            className={['webhooks-iconBtn', theme].join(' ')}
                          >
                            <span className="oi oi-ellipses" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!!selectedShipment && (
            <ShipmentEditor
              shipment={selectedShipment}
              theme={theme}
              destinationCountryCode={numericCountryToAlpha2(
                sortedCalculations.find(
                  (calc) =>
                    String(calc?.id ?? '') === String(selectedShipment?.calculation_id ?? ''),
                )?.delivery_address?.country,
              )}
              onClose={() => setSelectedShipment(null)}
              onDeleted={() => {
                setSelectedShipment(null);
                setReloadKey((key) => key + 1);
              }}
              onChanged={() => {
                setReloadKey((key) => key + 1);
              }}
            />
          )}
        </div>
      )}

      {createShipmentConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-shipment-warning-title"
          className={['webhooks-modalOverlay', 'webhooks-modalOverlay--nested', theme].join(' ')}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !createShipmentSubmitting) {
              setCreateShipmentConfirmOpen(false);
            }
          }}
        >
          <div className="webhooks-modal">
            <div className="webhooks-modalHeader">
              <div className="webhooks-modalTitle orders-createShipmentWarningTitle" id="create-shipment-warning-title">
                <span className="oi oi-warning" aria-hidden="true" />
                {t('ordersCreateShipmentWarningTitle')}
              </div>
              <button
                type="button"
                onClick={() => setCreateShipmentConfirmOpen(false)}
                className={['webhooks-iconBtn', theme].join(' ')}
                aria-label={t('Cancel')}
                title={t('Cancel')}
                disabled={createShipmentSubmitting}
              >
                ×
              </button>
            </div>
            <div className="webhooks-modalBody">
              <p className="orders-createShipmentWarningMessage">{t('ordersCreateShipmentWarningMessage')}</p>
            </div>
            <div className="webhooks-modalFooter">
              <button
                type="button"
                className={[
                  'webhooks-dangerBtn',
                  theme,
                  createShipmentSubmitting ? 'disabled' : 'enabled',
                ].join(' ')}
                onClick={executeCreateShipment}
                disabled={createShipmentSubmitting}
              >
                {createShipmentSubmitting ? t('loading') : t('ordersCreateShipmentWarningProceed')}
              </button>
              <button
                type="button"
                className={[
                  'webhooks-secondaryBtn',
                  theme,
                  createShipmentSubmitting ? 'disabled' : 'enabled',
                ].join(' ')}
                onClick={() => setCreateShipmentConfirmOpen(false)}
                disabled={createShipmentSubmitting}
              >
                {t('ordersCreateShipmentWarningAbort')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
